import { Component, Input} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { EventsService } from '../../../services/events.service';
import { Event, EventAdapter } from '../../../models/event';
import { map, Observable } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-events',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './events.component.html',
  styleUrl: './events.component.scss'
})
export class EventsComponent {

  @Input() itemTypeEvents: string = '';
  @Input() itemId: number = 0;
  events: Event[]=[];
  showRawDetailsMap: Record<number, boolean> = {};
  isLoading = false;
  hasBeenLoaded = false;
  errorMessage: string | null = null;

  constructor(private eventsService: EventsService,
    private eventAdapter: EventAdapter) { }
    
    public reloadEvents() {
      this.hasBeenLoaded = false;
      this.fetchEvents();
    }

    /**
     * Fetches the list of events of the role from the server.
     * 
     * @returns An observable that emits an array of Event objects
     */
    fetchEvents() {
    this.isLoading = true;
    this.getEventsObservable().pipe(
      map((data: any[]) => data.map(item => this.eventAdapter.adapt(item)))
    ).subscribe({
      next: (res: Event[]) => {
        this.events = res;
        this.isLoading = false;
        this.hasBeenLoaded = true;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = "Failed to load events.";
        console.error("Error fetching events:", err);
      }
    });
  }

    private getEventsObservable(): Observable<any[]> {
    switch (this.itemTypeEvents) {
      case 'role':
        return this.eventsService.getRoleEvents(this.itemId);
      case 'skill':
        return this.eventsService.getSkillEvents(this.itemId);
      case 'team':
        return this.eventsService.getTeamEvents(this.itemId);
      case 'people':
        return this.eventsService.getPeopleEvents(this.itemId);
      default:
        console.error("Unknown itemTypeEvents: " + this.itemTypeEvents);
        return new Observable<any[]>(); // return an empty observable to avoid binding error
    }
  }

  
// Method to get icon based on event type
getIconForEvent(typeId: string): string {
    const iconMap: { [key: string]: string } = {
      'Role.create': 'bi bi-plus',
      'Role.update': 'bi bi-pencil-fill',
      'Role.delete': 'bi bi-trash-fill',
      'Skill.create': 'bi bi-plus',
      'Skill.update': 'bi bi-pencil-fill',
      'Team.create': 'bi bi-plus',
      'Team.update': 'bi bi-pencil-fill',
      'People.create': 'bi bi-plus',
      'People.update': 'bi bi-pencil-fill',
      'People.team.assignment': 'bi bi-person-plus-fill',
      'People.team.unassignment': 'bi bi-person-dash-fill',
      'People.team.assignment.update': 'bi bi-person-gear',
      'Team.person.add': 'bi bi-person-plus-fill',
      'Team.person.remove': 'bi bi-person-dash-fill',
      'Team.person.assignment.update': 'bi bi-person-gear',
    };
    return iconMap[typeId] || 'bi bi-info-circle-fill';
  }

  // Method to get color based on event type
  getIconColorForEvent(typeId: string): string {
    const colorMap: { [key: string]: string } = {
      'Role.create': 'var(--bs-success)',
      'Role.update': 'var(--bs-primary)',
      'Role.delete': 'var(--bs-danger)',
      'Skill.create': 'var(--bs-success)',
      'Skill.update': 'var(--bs-primary)',
      'Team.create': 'var(--bs-success)',
      'Team.update': 'var(--bs-primary)',
      'People.create': 'var(--bs-success)',
      'People.update': 'var(--bs-primary)',
      'People.team.assignment': 'var(--bs-success)',
      'People.team.unassignment': 'var(--bs-danger)',
      'People.team.assignment.update': 'var(--bs-primary)',
      'Team.person.add': 'var(--bs-success)',
      'Team.person.remove': 'var(--bs-danger)',
      'Team.person.assignment.update': 'var(--bs-primary)',
    };
    return colorMap[typeId] || 'var(--bs-secondary)';
  }

  getTranslationKeyForEventType(typeId: string): string {
    const keyMap: { [key: string]: string } = {
      'Role.create': 'ROLE.CREATE',
      'Role.update': 'ROLE.UPDATE',
      'Role.delete': 'ROLE.DELETE',
      'Skill.create': 'SKILL.CREATE',
      'Skill.update': 'SKILL.UPDATE',
      'Team.create': 'TEAM.CREATE',
      'Team.update': 'TEAM.UPDATE',
      'People.create': 'PEOPLE.CREATE',
      'People.update': 'PEOPLE.UPDATE',
    };
    return keyMap[typeId] || 'ROLE.UNKNOWN';
  }

  // Switch to display raw details or not
  toggleRawDetails(event: MouseEvent, eventId: number) {
    this.showRawDetailsMap[eventId] = !this.showRawDetailsMap[eventId];
    event.preventDefault();
  }

  // Format raw details as JSON string
  getRawDetails(event: Event): string {
    return JSON.stringify(event.properties, null, 2);
  }
}
